import { HttpMethods } from 'constants/http';

interface ConstructorConfig {
  baseUrl?: string;
  injectHeaders?: () => Promise<object>;
}

interface RequestConfig extends RequestInit {
  payload?: object;
  /** Bounds header injection and the fetch itself. Omit to preserve the existing no-timeout default. */
  timeoutMs?: number;
  /** Opt in to response status on a rejected JSON body; older callers retain their contract. */
  includeErrorStatus?: boolean;
}

export class ApiRequestTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms.`);
    this.name = 'ApiRequestTimeoutError';
  }
}

class APIService {
  baseUrl: string;
  injectHeaders?: () => Promise<object>;

  constructor({ baseUrl = '', injectHeaders }: ConstructorConfig) {
    this.baseUrl = baseUrl;
    this.injectHeaders = injectHeaders;
  }

  async request(
    endpoint: string,
    { payload, timeoutMs, includeErrorStatus, ...customConfig }: RequestConfig = {},
  ) {
    const headers = { 'content-type': 'application/json' };
    const externalSignal = customConfig.signal;
    const abortController = timeoutMs ? new AbortController() : null;
    const forwardAbort = () => abortController?.abort();
    if (abortController && externalSignal) {
      if (externalSignal.aborted) {
        abortController.abort();
      } else {
        externalSignal.addEventListener('abort', forwardAbort, { once: true });
      }
    }
    const config: RequestInit = {
      method: payload ? HttpMethods.Post : HttpMethods.Get,
      ...customConfig,
      ...(abortController && { signal: abortController.signal }),
      headers: {
        ...headers,
        ...customConfig.headers,
      },
    };

    const performRequest = async () => {
      if (this.injectHeaders) {
        const injectedHeaders = await this.injectHeaders();
        config.headers = {
          ...config.headers,
          ...injectedHeaders,
        };
      }

      if (payload) {
        config.body = JSON.stringify(payload);
      }

      const response = await fetch(`${this.baseUrl}/${endpoint}`, config);
      let data;
      try {
        data = await response.json();
      } catch (error) {
        if (includeErrorStatus && !response.ok)
          throw { httpStatus: response.status, message: 'Request did not complete.' };
        throw error;
      }
      if (response.ok) {
        return data;
      }
      // TODO: Log in sentry
      return Promise.reject(includeErrorStatus ? { ...data, httpStatus: response.status } : data);
    };

    if (!timeoutMs) return performRequest();

    const timeoutError = new ApiRequestTimeoutError(timeoutMs);
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        abortController?.abort();
        reject(timeoutError);
      }, timeoutMs);
    });

    try {
      return await Promise.race([performRequest(), timeout]);
    } finally {
      clearTimeout(timer!);
      externalSignal?.removeEventListener('abort', forwardAbort);
    }
  }

  get(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Get;
    return this.request(endpoint, config);
  }

  post(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Post;
    return this.request(endpoint, config);
  }

  put(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Put;
    return this.request(endpoint, config);
  }

  patch(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Patch;
    return this.request(endpoint, config);
  }

  delete(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Delete;
    return this.request(endpoint, config);
  }
}

export default APIService;
